import { Layout, Zap, ChefHat, ShieldCheck, CheckCircle2 } from "lucide-react";
import useScrollReveal from "@/hooks/useScrollReveal";

const TM7Section = () => {
  const sectionRef = useScrollReveal();

  const productImages = [
    { src: "https://customer-assets.emergentagent.com/job_thermomix-zuhause/artifacts/f7pfsi0x_93ecb212-b1d7-47c3-be7a-3bfe8935eb10.jpeg", alt: "Thermomix TM7 Paket Komplett Krefeld - Alle Zubehoerteile und Preis" },
    { src: "https://customer-assets.emergentagent.com/job_thermomix-zuhause/artifacts/obuh1ab0_0f5a3d0b-bec4-4082-b482-756585633b48.jpeg", alt: "Thermomix TM7 Varoma Dampfgarsystem Detail" },
    { src: "https://customer-assets.emergentagent.com/job_thermomix-zuhause/artifacts/ava63ucs_005e64a8-cd19-497f-8299-ecc3bd379d29.jpeg", alt: "Thermomix TM7 Cookidoo Touchscreen Rezeptsuche" },
    { src: "https://customer-assets.emergentagent.com/job_thermomix-zuhause/artifacts/mklkpkmz_26114650-a21b-43f9-98a1-2046bedb5088.jpeg", alt: "Thermomix TM7 HD Display Seitenansicht Krefeld" },
    { src: "https://customer-assets.emergentagent.com/job_thermomix-zuhause/artifacts/5h2ege8w_28a365de-d8a3-4b8c-a629-73f6f94a27c5.jpeg", alt: "Thermomix TM7 Frontansicht Display beleuchtet" },
  ];

  return (
    <section id="tm7" data-testid="tm7-section" className="py-24 md:py-32 bg-slate-900 text-white overflow-hidden" ref={sectionRef}>
      <div className="container-custom">
        {/* Top: Text + Hero Product Image */}
        <div className="lg:grid lg:grid-cols-2 lg:gap-20 items-center mb-20">
          <div className="reveal">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-4 italic">Innovation Pur</span>
            <h2 className="text-4xl sm:text-5xl font-bold mb-8 leading-tight font-[Outfit]">
              Thermomix® TM7 in Krefeld: Die neue Generation live erleben
            </h2>
            <div className="space-y-6 text-slate-400 leading-relaxed">
              <p>
                Der Thermomix® TM7 setzt neue Maßstäbe in der Küchentechnologie. In Krefeld ist die 
                Begeisterung groß, denn das Gerät wurde konsequent dort verbessert, wo es den größten 
                Nutzen im Alltag bringt. Das Herzstück ist das neue <strong className="text-white">10-Zoll HD Touch-Display</strong>. 
                Es ist nicht nur größer, sondern auch deutlich schärfer und reaktionsschneller.
              </p>
              <p>
                Ein weiterer Gamechanger für offene Wohnküchen in Krefelder Haushalten ist der neue 
                <strong className="text-white"> Silent-Mode Motor</strong>. Vorwerk hat es geschafft, die Geräuschentwicklung 
                massiv zu reduzieren. Zudem ermöglicht die neue <strong className="text-white">Open Cooking Funktion</strong> das 
                Kochen ohne Deckel.
              </p>
            </div>
            <div className="mt-12 grid grid-cols-2 gap-6">
              {[
                { t: '10" HD Display', icon: Layout },
                { t: "Flüster-Motor", icon: Zap },
                { t: "Open Cooking", icon: ChefHat },
                { t: "Smart-Isolierung", icon: ShieldCheck }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="text-emerald-400"><item.icon className="w-5 h-5" /></div>
                  <span className="text-sm font-bold uppercase tracking-widest">{item.t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-16 lg:mt-0 reveal reveal-delay-1">
            <div className="rounded-[2rem] p-2 overflow-hidden shadow-2xl border border-white/10 bg-white/5">
              <img
                src={productImages[4].src}
                alt={productImages[4].alt}
                className="w-full aspect-[4/3] object-cover rounded-[1.5rem]"
              />
            </div>
          </div>
        </div>

        {/* Product Image Gallery */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20 reveal">
          <div className="md:col-span-2 md:row-span-2 rounded-[2rem] overflow-hidden border border-white/10 bg-white/5 shadow-xl">
            <img src={productImages[0].src} alt={productImages[0].alt} className="w-full h-full object-cover" />
          </div>
          <div className="rounded-[1.5rem] overflow-hidden border border-white/10 bg-white/5">
            <img src={productImages[1].src} alt={productImages[1].alt} className="w-full aspect-square object-cover" />
          </div>
          <div className="rounded-[1.5rem] overflow-hidden border border-white/10 bg-white/5">
            <img src={productImages[2].src} alt={productImages[2].alt} className="w-full aspect-square object-cover" />
          </div>
          <div className="rounded-[1.5rem] overflow-hidden border border-white/10 bg-white/5">
            <img src={productImages[3].src} alt={productImages[3].alt} className="w-full aspect-square object-cover" />
          </div>
          <div className="rounded-[1.5rem] overflow-hidden border border-white/10 bg-white/5">
            <img src={productImages[4].src} alt={productImages[4].alt} className="w-full aspect-square object-cover" />
          </div>
        </div>

        {/* Bottom: Smart Features */}
        <div className="lg:grid lg:grid-cols-2 lg:gap-20 items-center reveal">
          <div className="order-2 lg:order-1 rounded-[2rem] p-2 overflow-hidden shadow-2xl border border-white/10 bg-white/5">
            <img
              src={productImages[2].src}
              alt={productImages[2].alt}
              className="w-full aspect-[4/3] object-cover rounded-[1.5rem]"
            />
          </div>
          <div className="order-1 lg:order-2 mb-12 lg:mb-0">
            <h3 className="text-3xl sm:text-4xl font-bold mb-6 font-[Outfit]">Smart wie nie zuvor</h3>
            <p className="text-slate-400 mb-8 leading-relaxed">
              Mit dem TM7 wird dein Zuhause in Krefeld zur Smart-Kitchen. Die nahtlose Integration 
              von Cookidoo®, die verbesserte Waage und die smarten Kochmodi machen jede Mahlzeit 
              zum Erfolg. Ob du Anfänger bist oder Profi – der TM7 passt sich deinem Stil an.
            </p>
            <div className="space-y-4">
              {["Präzision in jedem Gramm", "Echtzeit-Updates & neue Modi", "Guided Cooking Weltweit", "Intelligente Energie-Effizienz"].map((t, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="text-white font-semibold">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TM7Section;
